// Heatmap screen: an isometric 3D treemap of a venue's market cap, or of every
// venue's on All venues (ported from radar's stock-heatmap). Footprint =
// market cap, colour = 24h change.
// Drag or arrow keys orbit, +/- zoom, hover for a tooltip, click to inspect.

import { useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

import { chainLabel, type Board } from "../api";
import { compactUsd, percent, price, signedPercent } from "../format";
import { layoutMarketCaps, OTHER_ID, prepareTiles, type HeatSource, type HeatTile } from "./heatmap-layout";
import { createHeatmapScene, DEFAULT_CAMERA, FULL_SCALE_CHANGE, type Camera, type HeatmapScene } from "./heatmap-scene";
import styles from "./Heatmap.module.css";

/** How many of the largest tokens the screen-reader list names. */
const ACCESSIBLE_LIST = 25;

const clampPitch = (pitch: number) => Math.max(0.25, Math.min(1.45, pitch));
const chainList = (chains: string[]) => chains.map(chainLabel).join(", ");
const changeText = (change: number | null) => (change === null ? "24h change not yet available" : `${signedPercent(change)} 24h`);
const changeClass = (change: number | null) => (change === null ? styles.flat : change > 0 ? "up" : change < 0 ? "down" : styles.flat);

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const query = matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(query.matches);
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  return reduced;
}

/** A venue's own board, or tokens gathered across venues (each tagged with its venue). */
type HeatmapProps = ({ board: Board } | { tokens: readonly HeatSource[]; scope: string }) & {
  /** Opens a token detail view from the inspector. */
  onSelect?: (id: string) => void;
};

/** "NVDAon · Ondo" on the market-wide map; the bare symbol on a venue's own. */
const tileLabel = (tile: HeatTile) => (tile.venue ? `${tile.symbol} · ${tile.venue}` : tile.symbol);

export function Heatmap(props: HeatmapProps) {
  const { onSelect } = props;
  const tokens = "board" in props ? props.board.tokens : props.tokens;
  const scope = "board" in props ? "this venue" : props.scope;
  const data = useMemo(() => prepareTiles(tokens), [tokens]);
  const blocks = useMemo(() => layoutMarketCaps(data.tiles), [data]);
  const total = useMemo(() => data.tiles.reduce((sum, t) => sum + t.mcap, 0), [data]);
  const missingChange = data.tiles.filter((t) => t.change === null).length;

  const [selected, setSelected] = useState("");
  const [camera, setCamera] = useState<Camera>(DEFAULT_CAMERA);
  const [motion, setMotion] = useState(true);
  const [relief, setRelief] = useState(true);
  const [hovered, setHovered] = useState<HeatTile | null>(null);
  const reduced = usePrefersReducedMotion();
  const listId = useId();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const scene = useRef<HeatmapScene | null>(null);
  const drag = useRef<{ x: number; y: number; startX: number; startY: number; moved: boolean } | null>(null);

  const active = data.tiles.find((t) => t.id === selected) ?? data.tiles[0];
  const rank = active ? data.tiles.indexOf(active) + 1 : 0;

  // The scene lives as long as the canvas does (the canvas is rendered only
  // while there are tiles). A 60-second poll brings new blocks, which are
  // swapped in place below rather than tearing the renderer down.
  const hasTiles = blocks.length > 0;
  const latestBlocks = useRef(blocks);
  latestBlocks.current = blocks;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasTiles) return;
    scene.current = createHeatmapScene(canvas, latestBlocks.current, {
      camera: DEFAULT_CAMERA,
      selected: latestBlocks.current[0]?.token.id ?? "",
      motion: true,
      relief: true,
    });
    return () => {
      scene.current?.dispose();
      scene.current = null;
    };
  }, [hasTiles]);

  useEffect(() => {
    scene.current?.setBlocks(blocks);
  }, [blocks]);

  useEffect(() => {
    scene.current?.update({ camera, selected: active?.id ?? "", motion, relief });
  }, [camera, active?.id, motion, relief, hasTiles]);

  const zoom = (amount: number) => setCamera((v) => ({ ...v, zoom: Math.max(0.55, Math.min(2.2, v.zoom + amount)) }));

  /** Hover hit test plus tooltip placement, without re-rendering on every move. */
  function hoverAt(e: ReactPointerEvent<HTMLCanvasElement>): void {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const tile = scene.current?.pointer(x, y) ?? null;
    setHovered((prev) => (prev?.id === (tile?.id ?? null) ? prev : tile));
    const tip = tooltipRef.current;
    if (tip && tile) {
      const w = tip.offsetWidth || 200;
      const h = tip.offsetHeight || 80;
      const left = Math.max(4, Math.min(rect.width - w - 4, x + 14));
      const top = y + 16 + h > rect.height ? Math.max(4, y - h - 12) : y + 16;
      tip.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
    }
  }

  function clearHover(): void {
    scene.current?.clearPointer();
    setHovered(null);
  }

  if (!active) {
    return (
      <section aria-label="Market cap heatmap" className={styles.root}>
        <p className={styles.empty}>
          No token on {scope} has a market cap yet, so there is nothing to map.
          {data.excluded ? ` ${data.excluded} token${data.excluded === 1 ? "" : "s"} without a market cap.` : ""}
        </p>
      </section>
    );
  }

  return (
    <section aria-label="Market cap heatmap" className={styles.root}>
      <header className={styles.head}>
        <h2 className={styles.title}>Market map</h2>
        <span className={styles.subtitle}>Footprint = market cap · colour = 24h price change</span>
      </header>

      <div className={styles.controls}>
        <label className={styles.pick}>
          <span className="sr-only">Inspect token</span>
          <select onChange={(e) => setSelected(e.target.value)} value={active.id}>
            {data.tiles.map((t) => (
              <option key={t.id} value={t.id}>
                {tileLabel(t)} · {compactUsd(t.mcap)}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.buttons}>
          <button aria-label="Zoom out" onClick={() => zoom(-0.15)} type="button">
            −
          </button>
          <button aria-label="Zoom in" onClick={() => zoom(0.15)} type="button">
            +
          </button>
          <button onClick={() => setCamera(DEFAULT_CAMERA)} type="button">
            Reset
          </button>
          <button aria-pressed={relief} onClick={() => setRelief(!relief)} type="button">
            Relief
          </button>
          <button
            aria-pressed={motion && !reduced}
            disabled={reduced}
            onClick={() => setMotion(!motion)}
            title={reduced ? "Motion is off: your system asks for reduced motion" : undefined}
            type="button"
          >
            Motion
          </button>
          <button disabled={!motion || reduced} onClick={() => scene.current?.pulse()} type="button">
            Ripple
          </button>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.stage}>
          <canvas
            aria-describedby={listId}
            aria-label={`3D market-cap heatmap of ${data.tiles.length} tiles. Drag or use arrow keys to rotate, plus and minus to zoom, Home to reset. The token selector and the list that follows give every value.`}
            className={styles.canvas}
            onKeyDown={(e) => {
              if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "+", "=", "-", "Home"].includes(e.key)) e.preventDefault();
              if (e.key === "Home") setCamera(DEFAULT_CAMERA);
              if (e.key === "+" || e.key === "=") zoom(0.15);
              if (e.key === "-") zoom(-0.15);
              if (e.key.startsWith("Arrow"))
                setCamera((v) => ({
                  ...v,
                  yaw: v.yaw + (e.key === "ArrowLeft" ? -0.12 : e.key === "ArrowRight" ? 0.12 : 0),
                  pitch: clampPitch(v.pitch + (e.key === "ArrowUp" ? 0.1 : e.key === "ArrowDown" ? -0.1 : 0)),
                }));
            }}
            onLostPointerCapture={() => {
              drag.current = null;
            }}
            onPointerCancel={() => {
              drag.current = null;
            }}
            onPointerDown={(e) => {
              if (!e.isPrimary || e.button !== 0) return;
              e.currentTarget.setPointerCapture(e.pointerId);
              hoverAt(e);
              drag.current = { x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY, moved: false };
            }}
            onPointerLeave={clearHover}
            onPointerMove={(e) => {
              if (!e.isPrimary) return;
              const prev = drag.current;
              if (!prev) {
                hoverAt(e);
                return;
              }
              const dx = e.clientX - prev.x;
              const dy = e.clientY - prev.y;
              if (!prev.moved && Math.hypot(e.clientX - prev.startX, e.clientY - prev.startY) > 4) {
                prev.moved = true;
                setHovered(null);
              }
              prev.x = e.clientX;
              prev.y = e.clientY;
              setCamera((v) => ({ ...v, yaw: v.yaw + dx * 0.006, pitch: clampPitch(v.pitch + dy * 0.006) }));
            }}
            onPointerUp={(e) => {
              if (!drag.current || !e.isPrimary) return;
              if (!drag.current.moved) {
                const rect = e.currentTarget.getBoundingClientRect();
                const id = scene.current?.pick(e.clientX - rect.left, e.clientY - rect.top);
                if (id) setSelected(id);
              }
              drag.current = null;
            }}
            ref={canvasRef}
            role="img"
            tabIndex={0}
          />
          <div aria-hidden className={styles.tooltip} hidden={!hovered} ref={tooltipRef}>
            {hovered ? (
              <>
                <strong>{tileLabel(hovered)}</strong>
                <span className={styles.tipName}>{hovered.name}</span>
                <span>
                  {compactUsd(hovered.mcap)} · {percent(hovered.mcap / total)} of map
                </span>
                {hovered.price !== null ? <span>{price(hovered.price)}</span> : null}
                <span className={changeClass(hovered.change)}>{changeText(hovered.change)}</span>
              </>
            ) : null}
          </div>
          <p aria-hidden className={styles.hint}>
            Drag to orbit · click to inspect · +/− zoom
          </p>
        </div>

        <aside aria-live="polite" className={styles.details}>
          <p className={styles.eyebrow}>
            Inspector <span>#{String(rank).padStart(3, "0")}</span>
          </p>
          <h3 className={styles.symbol}>{active.symbol}</h3>
          <p className={styles.name}>{active.name || "—"}</p>
          <div aria-hidden className={styles.shareTrack}>
            <span style={{ width: `${(active.mcap / total) * 100}%` }} />
          </div>
          <dl>
            <div>
              <dt>Market cap</dt>
              <dd>{compactUsd(active.mcap)}</dd>
            </div>
            <div>
              <dt>Share of map</dt>
              <dd>{percent(active.mcap / total)}</dd>
            </div>
            <div>
              <dt>{active.id === OTHER_ID ? "Tokens" : "Price"}</dt>
              <dd>{active.id === OTHER_ID ? active.count : active.price === null ? "—" : price(active.price)}</dd>
            </div>
            <div>
              <dt>24h change</dt>
              <dd className={changeClass(active.change)}>{signedPercent(active.change)}</dd>
            </div>
            {active.venue ? (
              <div className={styles.wide}>
                <dt>Venue</dt>
                <dd className={styles.small}>{active.venue}</dd>
              </div>
            ) : null}
            <div className={styles.wide}>
              <dt>Chains</dt>
              <dd className={styles.small}>{chainList(active.chains) || "—"}</dd>
            </div>
          </dl>
          {onSelect && active.id !== OTHER_ID ? (
            <button onClick={() => onSelect(active.id)} style={{ marginTop: 12 }} type="button">
              Open {active.symbol} details
            </button>
          ) : null}
        </aside>
      </div>

      <footer className={styles.legend}>
        <span className={styles.legendItem}>
          {signedPercent(-FULL_SCALE_CHANGE, 0)}
          <span aria-hidden className={styles.ramp} />
          {signedPercent(FULL_SCALE_CHANGE, 0)}
        </span>
        {missingChange ? (
          <span className={styles.legendItem}>
            <span aria-hidden className={styles.swatch} />
            24h change builds after a day of history ({missingChange} of {data.tiles.length} tiles)
          </span>
        ) : null}
        <span>
          {data.tiles.length - (data.folded ? 1 : 0)} {data.tiles.length - (data.folded ? 1 : 0) === 1 ? "token" : "tokens"} mapped
          {data.folded ? ` + other (${data.folded} smaller)` : ""} · {compactUsd(total)}
          {data.excluded ? ` · ${data.excluded} without market cap excluded` : ""}
        </span>
      </footer>

      <div className="sr-only" id={listId}>
        <h3>Largest tokens by market cap</h3>
        <ol>
          {data.tiles.slice(0, ACCESSIBLE_LIST).map((t) => (
            <li key={t.id}>
              {tileLabel(t)} {t.name ? `(${t.name})` : ""}: {compactUsd(t.mcap)}, {percent(t.mcap / total)} of mapped cap, {changeText(t.change)}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
